import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useState, type JSX } from 'react';

import { entryApi, EntryApiError } from '../api/entryApi';
import { useEntryStore } from '../store/useEntryStore';

interface CookieCategories {
  necessary: true;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

export function CookieBanner(): JSX.Element | null {
  const data = useEntryStore((s) => s.data);
  const refetch = useEntryStore((s) => s.refetch);
  const [expanded, setExpanded] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customCategories, setCustomCategories] = useState<CookieCategories>({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  if (!data || !data.cookie_consent.required) return null;

  const submit = async (categories: CookieCategories): Promise<void> => {
    setSubmitting(true);
    setError(null);
    try {
      await entryApi.submitCookieConsent({
        jurisdiction_tag: data.jurisdiction.tag,
        necessary: true,
        functional: categories.functional,
        analytics: categories.analytics,
        marketing: categories.marketing,
      });
      await refetch();
    } catch (err) {
      const message = err instanceof EntryApiError ? err.message : 'Could not save your choice. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const granular = data.cookie_consent.granular;

  if (!granular) {
    // Simplified single-action banner for non-protected jurisdictions.
    return (
      <Paper
        elevation={6}
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.snackbar,
          p: 2,
          borderRadius: 0,
        }}
        role="region"
        aria-label="Cookie notice"
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            We use cookies necessary for session and security. By continuing you acknowledge this.
          </Typography>
          <Button
            variant="contained"
            disabled={submitting}
            onClick={() => void submit({ necessary: true, functional: false, analytics: false, marketing: false })}
          >
            Got it
          </Button>
        </Stack>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </Paper>
    );
  }

  return (
    <Paper
      elevation={6}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.snackbar,
        p: 2,
        borderRadius: 0,
      }}
      role="region"
      aria-label="Cookie consent"
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="body2" sx={{ flex: 1 }}>
            We use cookies to provide essential functionality and, with your consent, to improve your experience. You
            can accept all, reject non-essential, or customize categories.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              variant="contained"
              disabled={submitting}
              onClick={() =>
                void submit({ necessary: true, functional: true, analytics: true, marketing: true })
              }
            >
              Accept All
            </Button>
            <Button
              variant="outlined"
              disabled={submitting}
              onClick={() =>
                void submit({ necessary: true, functional: false, analytics: false, marketing: false })
              }
            >
              Reject Non-Essential
            </Button>
            <Button
              variant="text"
              disabled={submitting}
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
            >
              Customize
            </Button>
          </Stack>
        </Stack>

        <Collapse in={expanded} unmountOnExit>
          <Box sx={{ pt: 1, borderTop: 1, borderColor: 'divider' }}>
            <Stack spacing={1} sx={{ mt: 1 }}>
              <FormControlLabel
                control={<Switch checked disabled />}
                label="Necessary (always on)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={customCategories.functional}
                    onChange={(_, checked) => setCustomCategories((c) => ({ ...c, functional: checked }))}
                  />
                }
                label="Functional (preferences, theme)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={customCategories.analytics}
                    onChange={(_, checked) => setCustomCategories((c) => ({ ...c, analytics: checked }))}
                  />
                }
                label="Analytics (usage telemetry)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={customCategories.marketing}
                    onChange={(_, checked) => setCustomCategories((c) => ({ ...c, marketing: checked }))}
                  />
                }
                label="Marketing (personalized offers)"
              />
              <Box sx={{ pt: 1 }}>
                <Button
                  variant="contained"
                  disabled={submitting}
                  onClick={() => void submit(customCategories)}
                >
                  Save preferences
                </Button>
              </Box>
            </Stack>
          </Box>
        </Collapse>
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
}
